import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Filter, X, RotateCcw, Check } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const ChallengeFilters = ({ onClose }) => {
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedDifficulties, setSelectedDifficulties] = useState([]);
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [pointRange, setPointRange] = useState([0, 1000]);
  const [timeRange, setTimeRange] = useState([0, 120]);
  const [completionRate, setCompletionRate] = useState([0, 100]);

  const categories = [
    { id: 'ui-automation', label: 'UI Automation', count: 25 },
    { id: 'api-testing', label: 'API Testing', count: 18 },
    { id: 'manual-testing', label: 'Manual Testing', count: 22 },
    { id: 'performance', label: 'Performance Testing', count: 12 },
    { id: 'security', label: 'Security Testing', count: 8 },
    { id: 'mobile', label: 'Mobile Testing', count: 10 },
    { id: 'accessibility', label: 'Accessibility Testing', count: 6 },
    { id: 'database', label: 'Database Testing', count: 5 }
  ];

  const difficulties = [
    { id: 'beginner', label: 'Beginner', count: 25, color: 'bg-green-100 text-green-700' },
    { id: 'intermediate', label: 'Intermediate', count: 35, color: 'bg-blue-100 text-blue-700' },
    { id: 'advanced', label: 'Advanced', count: 30, color: 'bg-purple-100 text-purple-700' },
    { id: 'expert', label: 'Expert', count: 15, color: 'bg-red-100 text-red-700' }
  ];

  const industries = [
    { id: 'general', label: 'General', count: 40 },
    { id: 'ecommerce', label: 'E-commerce', count: 15 },
    { id: 'fintech', label: 'FinTech', count: 12 },
    { id: 'healthcare', label: 'Healthcare', count: 8 },
    { id: 'saas', label: 'SaaS', count: 10 },
    { id: 'media', label: 'Media', count: 6 },
    { id: 'government', label: 'Government', count: 4 },
    { id: 'enterprise', label: 'Enterprise', count: 9 }
  ];

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories(prev => 
      prev?.includes(categoryId)
        ? prev?.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleDifficultyToggle = (difficultyId) => {
    setSelectedDifficulties(prev => 
      prev?.includes(difficultyId)
        ? prev?.filter(id => id !== difficultyId)
        : [...prev, difficultyId]
    );
  };

  const handleIndustryToggle = (industryId) => {
    setSelectedIndustries(prev => 
      prev?.includes(industryId)
        ? prev?.filter(id => id !== industryId)
        : [...prev, industryId]
    );
  };

  const clearAllFilters = () => {
    setSelectedCategories([]);
    setSelectedDifficulties([]);
    setSelectedIndustries([]);
    setPointRange([0, 1000]);
    setTimeRange([0, 120]);
    setCompletionRate([0, 100]);
  };

  const getActiveFiltersCount = () => {
    return selectedCategories?.length + 
           selectedDifficulties?.length + 
           selectedIndustries?.length +
           (pointRange?.[0] > 0 || pointRange?.[1] < 1000 ? 1 : 0) +
           (timeRange?.[0] > 0 || timeRange?.[1] < 120 ? 1 : 0) +
           (completionRate?.[0] > 0 || completionRate?.[1] < 100 ? 1 : 0);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Filter className="w-5 h-5" />
          <h3 className="font-semibold">Advanced Filters</h3>
          {getActiveFiltersCount() > 0 && (
            <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">
              {getActiveFiltersCount()}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            className="flex items-center space-x-1"
          >
            <RotateCcw className="w-3 h-3" />
            <span>Reset</span>
          </Button>
          {onClose && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
      {/* Categories */}
      <div>
        <h4 className="font-medium mb-3">Categories</h4>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {categories?.map((category) => (
            <div key={category?.id} className="flex items-center space-x-3">
              <Checkbox
                checked={selectedCategories?.includes(category?.id)}
                onChange={() => handleCategoryToggle(category?.id)}
              />
              <label className="flex-1 text-sm cursor-pointer flex items-center justify-between">
                <span>{category?.label}</span>
                <span className="text-xs text-muted-foreground">({category?.count})</span>
              </label>
            </div>
          ))}
        </div>
      </div>
      {/* Difficulty Levels */}
      <div>
        <h4 className="font-medium mb-3">Difficulty Levels</h4>
        <div className="space-y-2">
          {difficulties?.map((difficulty) => (
            <div key={difficulty?.id} className="flex items-center space-x-3">
              <Checkbox
                checked={selectedDifficulties?.includes(difficulty?.id)}
                onChange={() => handleDifficultyToggle(difficulty?.id)}
              />
              <label className="flex-1 text-sm cursor-pointer flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span>{difficulty?.label}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${difficulty?.color}`}>
                    {difficulty?.count}
                  </span>
                </div>
              </label>
            </div>
          ))}
        </div>
      </div>
      {/* Industries */}
      <div>
        <h4 className="font-medium mb-3">Industries</h4>
        <div className="space-y-2 max-h-32 overflow-y-auto">
          {industries?.map((industry) => (
            <div key={industry?.id} className="flex items-center space-x-3">
              <Checkbox
                checked={selectedIndustries?.includes(industry?.id)}
                onChange={() => handleIndustryToggle(industry?.id)}
              />
              <label className="flex-1 text-sm cursor-pointer flex items-center justify-between">
                <span>{industry?.label}</span>
                <span className="text-xs text-muted-foreground">({industry?.count})</span>
              </label>
            </div>
          ))}
        </div>
      </div>
      {/* Point Range */}
      <div>
        <h4 className="font-medium mb-3">Points Range</h4>
        <div className="space-y-2">
          <div className="flex items-center space-x-4">
            <input
              type="range"
              min="0"
              max="1000"
              value={pointRange?.[0]}
              onChange={(e) => setPointRange([parseInt(e?.target?.value), pointRange?.[1]])}
              className="flex-1"
            />
          </div>
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>{pointRange?.[0]} pts</span>
            <span>{pointRange?.[1]} pts</span>
          </div>
        </div>
      </div>
      {/* Time Limit Range */}
      <div>
        <h4 className="font-medium mb-3">Time Limit (minutes)</h4>
        <div className="space-y-2">
          <div className="flex items-center space-x-4">
            <input
              type="range"
              min="0"
              max="120"
              value={timeRange?.[0]}
              onChange={(e) => setTimeRange([parseInt(e?.target?.value), timeRange?.[1]])}
              className="flex-1"
            />
          </div>
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>{timeRange?.[0]} min</span>
            <span>{timeRange?.[1]} min</span>
          </div>
        </div>
      </div>
      {/* Completion Rate */}
      <div>
        <h4 className="font-medium mb-3">Success Rate</h4>
        <div className="space-y-2">
          <div className="flex items-center space-x-4">
            <input
              type="range"
              min="0"
              max="100"
              value={completionRate?.[0]}
              onChange={(e) => setCompletionRate([parseInt(e?.target?.value), completionRate?.[1]])}
              className="flex-1"
            />
          </div>
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>{completionRate?.[0]}%</span>
            <span>{completionRate?.[1]}%</span>
          </div>
        </div>
      </div>
      {/* Apply Filters Button */}
      <div className="pt-4 border-t border-border">
        <Button 
          size="sm" 
          className="w-full flex items-center justify-center space-x-2"
          onClick={onClose}
        >
          <Check className="w-4 h-4" />
          <span>Apply Filters ({getActiveFiltersCount()})</span>
        </Button>
      </div>
    </motion.div>
  );
};

export default ChallengeFilters;